/**
 * SeatDetail Service
 * 좌석 상세 정보 비즈니스 로직
 */

export class SeatDetailService {
  /**
   * 시간(분)을 포맷팅 (예: 125분 → 2시간 5분)
   */
  formatDuration(minutes: number): string {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}시간 ${mins}분` : `${mins}분`;
  }

  /**
   * 날짜/시간 포맷팅
   */
  formatDateTime(dateTimeString: string): string {
    const date = new Date(dateTimeString);
    return date.toLocaleString('ko-KR');
  }

  /**
   * 좌석 상태 텍스트 반환
   */
  getSeatStatusText(isOutOfOrder: boolean, isAvailable: boolean): string {
    if (isOutOfOrder) return '점검/수리 중';
    if (isAvailable) return '사용 가능';
    return '사용 중';
  }

  /**
   * 좌석 상태 색상 클래스 반환
   */
  getSeatStatusColor(isOutOfOrder: boolean, isAvailable: boolean): string {
    if (isOutOfOrder) return 'text-red-600';
    if (isAvailable) return 'text-green-600';
    return 'text-blue-600';
  }

  /**
   * 이용권 타입 라벨 반환
   */
  getMembershipTypeLabel(type: string): string {
    const labels: Record<string, string> = {
      hourly: '시간권',
      daily: '일권',
      weekly: '주간권',
      monthly: '월간권',
    };
    return labels[type] || type;
  }

  /**
   * 학습 효율 계산 (순공부시간 / 전체시간 * 100)
   */
  calculateStudyEfficiency(pureStudyMinutes: number, totalMinutes: number): number {
    if (totalMinutes <= 0) return 0;
    return Math.round((pureStudyMinutes / totalMinutes) * 100);
  }

  /**
   * 학습 효율 바 색상 반환
   */
  getEfficiencyBarColor(efficiency: number): string {
    if (efficiency >= 80) return 'bg-blue-600';
    if (efficiency >= 60) return 'bg-green-600';
    if (efficiency >= 40) return 'bg-yellow-600';
    return 'bg-orange-600';
  }

  /**
   * 강제 퇴실 확인 필요 여부
   */
  needsForceCheckoutConfirmation(actionType: string): boolean {
    return actionType === 'force_checkout';
  }

  /**
   * 액션 버튼 표시 여부
   */
  shouldShowForceCheckout(
    isOutOfOrder: boolean,
    isAvailable: boolean,
    hasCurrentSession: boolean
  ): boolean {
    return !isOutOfOrder && !isAvailable && hasCurrentSession;
  }

  /**
   * 점검 모드 토글 버튼 텍스트
   */
  getMaintenanceButtonText(isOutOfOrder: boolean): string {
    return isOutOfOrder ? '수리 완료' : '점검 모드';
  }

  /**
   * 점검 모드 토글 버튼 색상 클래스
   */
  getMaintenanceButtonColor(isOutOfOrder: boolean): string {
    return isOutOfOrder ? 'bg-green-600 hover:bg-green-700' : 'bg-orange-600 hover:bg-orange-700';
  }
}

export const seatDetailService = new SeatDetailService();
